import axios from 'axios';
import { isEncryptionEnabled, encryptPayload, decryptPayload } from '../utils/crypto';

// Simple global rate-limit event bus
const rateLimitListeners = new Set();
export function onRateLimit(listener) {
  rateLimitListeners.add(listener);
  return () => rateLimitListeners.delete(listener);
}
function emitRateLimit(event) {
  rateLimitListeners.forEach((l) => {
    try {
      l(event);
    } catch (_) {}
  });
}

// Concurrency gate (semaphore)
const MAX_CONCURRENT = 6;
let activeCount = 0;
const queue = [];
function acquire() {
  return new Promise((resolve) => {
    const tryAcquire = () => {
      if (activeCount < MAX_CONCURRENT) {
        activeCount += 1;
        resolve(() => {
          activeCount -= 1;
          const next = queue.shift();
          if (next) next();
        });
      } else {
        queue.push(tryAcquire);
      }
    };
    tryAcquire();
  });
}

// In-flight request deduplication map
const inflight = new Map();
function fingerprint(config) {
  const { method = 'get', url = '', params, data } = config || {};
  const sortedParams =
    params && typeof params === 'object'
      ? JSON.stringify(
          Object.keys(params)
            .sort()
            .reduce((acc, k) => {
              acc[k] = params[k];
              return acc;
            }, {})
        )
      : '';
  const dataStr = typeof data === 'string' ? data : data ? JSON.stringify(data) : '';
  return `${method.toLowerCase()} ${url}?p=${sortedParams}&d=${dataStr}`;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function parseRetryAfterMs(h) {
  if (!h) return null;
  const n = Number(h);
  if (!Number.isNaN(n)) return Math.max(0, n * 1000);
  const d = Date.parse(h);
  return Number.isNaN(d) ? null : Math.max(0, d - Date.now());
}

function expBackoffDelayMs(attempt, base = 300, cap = 10_000) {
  const exp = Math.min(cap, base * Math.pow(2, attempt));
  const jitter = Math.random() * 0.3 * exp;
  return Math.min(cap, exp + jitter);
}

let hasShownRateToastRecently = false;
let lastToastAt = 0;
const TOAST_COOLDOWN_MS = 20_000;

// Shared refresh-in-flight promise so concurrent 401s only trigger one refresh
let refreshInFlight = null;
async function attemptTokenRefresh() {
  if (refreshInFlight) return refreshInFlight;
  const storedRefresh = localStorage.getItem('refreshToken');
  if (!storedRefresh) return null;

  refreshInFlight = (async () => {
    try {
      const { data } = await axios.post('/api/auth/refresh', {
        refreshToken: storedRefresh,
      });
      localStorage.setItem('token', data.token);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      return data.token;
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export const http = axios.create({ withCredentials: true });

http.interceptors.request.use(async (config) => {
  // Initialize headers if needed
  config.headers = config.headers || {};

  // Add Authorization header from localStorage
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // ── AES-256-GCM encryption (production only) ──────────────────────
  // Multipart/FormData requests are left entirely untouched: the body is never
  // encrypted, and adding the x-encrypted header would only force a CORS
  // preflight that some in-app/mobile browsers fail, breaking file uploads.
  if (isEncryptionEnabled() && !(config.data instanceof FormData)) {
    // Tell the backend we want encrypted responses (GET, POST, etc.)
    config.headers['x-encrypted'] = 'true';

    // Encrypt request body when present (POST/PUT/PATCH with JSON)
    if (config.data && typeof config.data === 'object') {
      try {
        const encrypted = await encryptPayload(config.data);
        config.data = encrypted;
      } catch (err) {
        console.error('[crypto] Failed to encrypt request:', err);
        // Fall through with unencrypted body so the request isn't silently lost
      }
    }
  }

  // Concurrency gate
  const release = await acquire();
  config.__release = release;

  // Deduplicate
  const key = fingerprint(config);
  if (inflight.has(key)) {
    return inflight.get(key);
  }
  config.__fingerprint = key;
  inflight.set(key, Promise.resolve(config));
  return config;
});

http.interceptors.response.use(
  async (response) => {
    const release = response.config.__release;
    if (typeof release === 'function') release();
    if (response.config.__fingerprint) inflight.delete(response.config.__fingerprint);

    // ── AES-256-GCM decryption (production only) ────────────────────
    if (
      isEncryptionEnabled() &&
      response.data &&
      typeof response.data === 'object' &&
      response.data.iv &&
      response.data.data &&
      response.data.tag &&
      Object.keys(response.data).length === 3
    ) {
      try {
        response.data = await decryptPayload(response.data);
      } catch (err) {
        console.error('[crypto] Failed to decrypt response:', err);
      }
    }

    return response;
  },
  async (error) => {
    const config = error?.config || {};
    const release = config.__release;
    if (typeof release === 'function') release();
    if (config.__fingerprint) inflight.delete(config.__fingerprint);

    // In production, the backend may encrypt *error* payloads too.
    // Decrypt them here so UI code can reliably access `err.response.data.message`.
    if (
      isEncryptionEnabled() &&
      error?.response?.data &&
      typeof error.response.data === 'object' &&
      error.response.data.iv &&
      error.response.data.data &&
      error.response.data.tag &&
      Object.keys(error.response.data).length === 3
    ) {
      try {
        error.response.data = await decryptPayload(error.response.data);
      } catch (err) {
        console.error('[crypto] Failed to decrypt error response:', err);
      }
    }

    const status = error?.response?.status;

    // Auto-refresh on 401 (expired access token)
    if (status === 401 && !config._retried401) {
      config._retried401 = true;
      const newToken = await attemptTokenRefresh();
      if (newToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
        return http(config);
      }
      return Promise.reject(error);
    }

    if (status !== 429) {
      return Promise.reject(error);
    }

    // Show a single toast/banner notification per cooldown
    const now = Date.now();
    if (!hasShownRateToastRecently || now - lastToastAt > TOAST_COOLDOWN_MS) {
      hasShownRateToastRecently = true;
      lastToastAt = now;
      emitRateLimit({ type: 'throttled' });
    }

    // Retry with backoff respecting Retry-After
    config.__retryCount = (config.__retryCount || 0) + 1;
    const maxRetries = 5;
    if (config.__retryCount > maxRetries) {
      const structuredError = new Error('Rate limited: retries exhausted');
      structuredError.name = 'RateLimitError';
      structuredError.status = 429;
      structuredError.retryAfter =
        parseRetryAfterMs(error?.response?.headers?.['retry-after']) ?? null;
      return Promise.reject(structuredError);
    }

    const headerDelay = parseRetryAfterMs(error?.response?.headers?.['retry-after']);
    const attemptDelay = expBackoffDelayMs(config.__retryCount);
    const delay = headerDelay != null ? headerDelay : attemptDelay;
    await sleep(delay);
    return http(config);
  }
);

export default http;
